import styled, { css } from 'styled-components';
import {
    ANIMATION_CURVE,
    ANIMATION_DURATION,
    FOCUS_OUTLINE_STYLE,
    getSpacingToken,
    getToken,
} from '@optimus-web/common';
import { CheckboxContainer } from '../Checkbox/style';
import { Grid } from '../Grid';
import { RadioElement } from '../Radio/style';
import { Stack } from '../Stack';
import { Typography } from '../typography';
import { Direction } from './types';

interface SelectionCardContainerProps {
    $direction: Direction;
    checked: boolean;
    disabled: boolean;
    cornerRadius: 'small' | 'medium';
    focusVisible: boolean;
}

export const SelectionCardContainer = styled(Grid)<SelectionCardContainerProps>`
    border-radius: ${getToken('borderRadius200')}px;
    border: ${getToken('borderWidth150')}px solid ${getToken('borderInteractiveSecondaryDefault')};
    background-color: ${getToken('backgroundStaticFlat')};
    transition-property: color, border-color, background-color;
    transition-timing-function: ${ANIMATION_CURVE.default};
    transition-duration: ${ANIMATION_DURATION.fast}ms;
    padding: ${({ $direction, theme }) =>
        $direction === 'horizontal'
            ? `${getSpacingToken('200')({ theme })}px`
            : `${getSpacingToken('300')({ theme })}px ${getSpacingToken('100')({ theme })}px`};
    overflow-wrap: anywhere;
    position: relative;

    @media (hover: hover) {
        &:hover {
            border-color: ${getToken('borderInteractiveSecondaryHover')};
        }
    }

    &:active {
        border-color: ${getToken('borderInteractiveSecondaryActive')};
    }

    ${({ $direction }) =>
        $direction === 'vertical' &&
        css`
            justify-items: center;
            align-content: start;
            justify-content: stretch;

            ${LabelContainer} {
                align-items: center;
                text-align: center;
                grid-row: 2/3;
            }

            ${OptionTypeContainer} {
                justify-self: end;
                grid-row: 1/1;
                grid-column: 1;
            }

            ${OptionTypeContainer} + ${SelectionCardMediaElement}, ${OptionTypeContainer} + ${LabelContainer} {
                grid-row: 1/2;
                grid-column: 1;
            }
        `}

    ${({ checked }) =>
        checked &&
        css`
            background-color: ${getToken('backgroundInteractiveSecondaryDefault')};
            border-color: ${getToken('borderInteractivePrimaryDefault')};

            &:hover {
                background-color: ${getToken('backgroundInteractiveSecondaryHover')};
                border-color: ${getToken('borderInteractivePrimaryHover')};
            }

            &:active {
                background-color: ${getToken('backgroundInteractiveSecondaryActive')};
                border-color: ${getToken('borderInteractivePrimaryActive')};
            }
        `}

    ${({ disabled }) =>
        disabled
            ? css`
                  border-color: ${getToken('borderDisabled')};
                  pointer-events: none;

                  & *,
                  & path {
                      color: ${getToken('textDisabled')};
                      fill: ${getToken('textDisabled')};
                      border-color: ${getToken('borderDisabled')};
                  }
              `
            : css`
                  cursor: pointer;
              `}

    ${({ cornerRadius }) =>
        cornerRadius === 'medium'
            ? css`
                  border-radius: ${getToken('borderRadius200')}px;
              `
            : css`
                  border-radius: ${getToken('borderRadius100')}px;
              `}

    ${({ focusVisible }) => focusVisible && FOCUS_OUTLINE_STYLE}
`;

export const SelectionCardMediaElement = styled(Stack)<{ order: number }>`
    order: ${({ order }) => order};
`;

export const LabelContainer = styled(Stack)``;

export const Label = styled(Typography)`
    width: 100%;
`;

export const OptionTypeContainer = styled(Stack)<{ $direction: Direction; showSelector: boolean }>`
    ${({ showSelector }) =>
        !showSelector &&
        css`
            opacity: ${getToken('opacity0')};
            position: absolute;
        `}
    top: 0;
    right: 0;
    ${({ $direction }) =>
        $direction === 'vertical' &&
        css`
            position: absolute;
            top: -${getSpacingToken('150')}px;
            right: ${getSpacingToken('200')}px;
        `};
    ${RadioElement}, ${CheckboxContainer} {
        pointer-events: none;
    }
    & > label > div {
        margin-right: 0;
    }
`;
